import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Typography } from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { useContextMenu } from '@/components/ContextMenu';
import ContextMenuProvider from '@/components/ContextMenu/ContextMenuProvider';
import useGridTheme from '@/hooks/useGridTheme';
import { getProductCell } from '@/pages/Administration/AdministrationProducts';
import type {
  Organization,
  ProductDTO
} from '@/pages/Organizations/Organizations.types';

import type { ProductEditDialogMode } from '../ProductEditDialog';
import { PRODUCTS_CONTEXT_MENU_ITEMS } from './OrganizationsTable.constants';
import type { TableColumn } from './OrganizationsTable.types';

type GridContext = {
  openProductDialog: (
    mode: ProductEditDialogMode,
    product?: ProductDTO
  ) => void;
};

const makeProductCellRenderer = (columnId: string) => {
  const Renderer = ({ data }: ICellRendererParams<ProductDTO>) =>
    data ? getProductCell({ id: columnId } as TableColumn, data) : null;
  Renderer.displayName = `ProductCell_${columnId}`;
  return Renderer;
};

const ProductNameRenderer = ({ data }: ICellRendererParams<ProductDTO>) =>
  data ? data.product_name : null;

const ProductTypeRenderer = makeProductCellRenderer('product_type');
const ProductSubscriptionRenderer = makeProductCellRenderer('subscription');
const ProductStartDateRenderer = makeProductCellRenderer('start_date');
const ProductEndDateRenderer = makeProductCellRenderer('end_date');

const ProductActionCellRenderer = ({
  data
}: ICellRendererParams<ProductDTO>) => {
  const { showContextMenu } = useContextMenu();
  if (!data) return null;
  return (
    <Button variant="text" onClick={(e) => showContextMenu(e, `${data.id}`)}>
      <FontAwesomeIcon icon={faEllipsisV} />
    </Button>
  );
};

const PRODUCT_COLUMN_DEFS: ColDef[] = [
  {
    field: 'product_name',
    headerName: 'Product Name',
    flex: 2,
    cellRenderer: ProductNameRenderer
  },
  {
    field: 'product_type',
    headerName: 'Product Type',
    flex: 1,
    cellRenderer: ProductTypeRenderer
  },
  {
    field: 'subscription',
    headerName: 'Subscription',
    flex: 1,
    cellRenderer: ProductSubscriptionRenderer,
    sortable: false
  },
  {
    field: 'start_date',
    headerName: 'Product Start',
    width: 150,
    cellRenderer: ProductStartDateRenderer
  },
  {
    field: 'end_date',
    headerName: 'Product End',
    width: 160,
    cellRenderer: ProductEndDateRenderer
  },
  {
    headerName: '',
    width: 70,
    sortable: false,
    suppressHeaderMenuButton: true,
    cellRenderer: ProductActionCellRenderer
  }
];

const OrganizationProductsDetailRenderer = (
  props: ICellRendererParams<Organization> & { context: GridContext }
) => {
  const gridTheme = useGridTheme();
  const { data, context } = props;

  const handleProductMenuClick = (action: string, id?: string) => {
    const product = data?.products?.find((p) => p.id === +(id || 0));
    if (product) {
      context.openProductDialog(action as ProductEditDialogMode, product);
    }
  };

  return (
    <Stack
      direction="row"
      sx={{ p: 2, backgroundColor: 'background.elevation3' }}
    >
      <Typography
        variant="body1"
        sx={{
          writingMode: 'vertical-rl',
          display: 'flex',
          justifyContent: 'center',
          mr: 2
        }}
      >
        PRODUCTS
      </Typography>
      <div style={{ flex: 1 }}>
        <ContextMenuProvider
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          menuItems={PRODUCTS_CONTEXT_MENU_ITEMS}
          clickHandler={handleProductMenuClick}
        >
          <AgGridReact
            rowData={data?.products || []}
            columnDefs={PRODUCT_COLUMN_DEFS}
            defaultColDef={{ suppressHeaderMenuButton: true }}
            theme={gridTheme}
            domLayout="autoHeight"
            suppressCellFocus
          />
        </ContextMenuProvider>
      </div>
    </Stack>
  );
};

export default OrganizationProductsDetailRenderer;
